/**
 * Global Listing Configuration Manager
 * Admin page to manage global listing configurations that serve as fallbacks for all tenants
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Save,
  RefreshCw,
  Eye,
  Edit,
  Settings,
  Globe,
  AlertCircle,
  CheckCircle,
  Info,
  Download,
  Upload,
  Copy,
  Trash2,
  Plus,
  X
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const GlobalListingConfigManager = () => {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState('Customer');
  const [selectedTab, setSelectedTab] = useState(null);
  const [availableTabs, setAvailableTabs] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [availableFields, setAvailableFields] = useState({});
  const [fieldsLoading, setFieldsLoading] = useState(false);

  const availableEntities = [
    { name: 'Customer', label: 'Customers', icon: '👥', color: 'blue' },
    { name: 'Lead', label: 'Leads', icon: '🎯', color: 'green' },
    { name: 'Opportunity', label: 'Opportunities', icon: '💼', color: 'purple' },
    { name: 'Activity', label: 'Activities', icon: '📅', color: 'orange' }
  ];

  useEffect(() => {
    loadGlobalConfigs();
  }, []);

  useEffect(() => {
    // Load available entity fields when entity changes
    const loadFields = async () => {
      try {
        setFieldsLoading(true);
        const res = await fetch(`/api/entity-config/${selectedEntity}/fields`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!res.ok) {
          throw new Error(`Failed to load fields for ${selectedEntity}`);
        }
        const data = await res.json();
        // data.fields is an object keyed by fieldName → fieldConfig
        setAvailableFields(data.fields || {});
      } catch (err) {
        console.error('❌ Error loading available fields:', err);
        setAvailableFields({});
      } finally {
        setFieldsLoading(false);
      }
    };
    loadFields();
  }, [selectedEntity]);

  useEffect(() => {
    // Load tab information when entity or configs change
    if (Object.keys(configs).length > 0) {
      loadTabInfo();
    }
  }, [selectedEntity, configs]);

  const loadGlobalConfigs = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading global configurations...');
      
      const response = await fetch('/api/admin/global-listing-configs', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Error response:', errorText);
        throw new Error(`Failed to load global configurations: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('🔍 Received data:', data);
      console.log('🔍 Configs:', data.configs);
      
      setConfigs(data.configs || {});
      console.log('🔍 Configs set in state:', data.configs || {});
      
      // Load tab information for the selected entity
      await loadTabInfo();
    } catch (error) {
      console.error('❌ Error loading global configs:', error);
      
      // Fallback: Try to load from a public endpoint or show error
      console.log('🔍 Attempting fallback loading...');
      try {
        // For development, we could try to load from a public endpoint
        // or show a message to the user
        toast.error(`Failed to load global configurations: ${error.message}`);
        
        // In development mode, we could try to load from a public endpoint
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Development mode: Attempting to load from public endpoint...');
          try {
            const publicResponse = await fetch('/api/public/global-listing-configs');
            if (publicResponse.ok) {
              const publicData = await publicResponse.json();
              console.log('🔍 Public endpoint data:', publicData);
              setConfigs(publicData.configs || {});
              toast.success('Loaded configurations from public endpoint (development mode)');
              return;
            }
          } catch (publicError) {
            console.error('❌ Public endpoint also failed:', publicError);
          }
        }
      } catch (fallbackError) {
        console.error('❌ Fallback also failed:', fallbackError);
        toast.error('Failed to load global configurations. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTabInfo = async () => {
    try {
      const config = configs[selectedEntity];
      if (config && config.supportsTabs && config.tabs) {
        const tabs = Object.entries(config.tabs).map(([key, tab]) => ({
          key,
          label: tab.label,
          filter: tab.filter
        }));
        setAvailableTabs(tabs);
        // Reset selected tab when entity changes
        setSelectedTab(null);
      } else {
        setAvailableTabs([]);
        setSelectedTab(null);
      }
    } catch (error) {
      console.error('Error loading tab info:', error);
      setAvailableTabs([]);
      setSelectedTab(null);
    }
  };

  const saveGlobalConfigs = async () => {
    try {
      setSaving(true);
      const response = await fetch('/api/admin/global-listing-configs', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ configs })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save global configurations');
      }

      toast.success('Global configurations saved successfully');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving global configs:', error);
      toast.error(error.message || 'Failed to save global configurations');
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (entityName, field, value) => {
    setConfigs(prev => ({
      ...prev,
      [entityName]: {
        ...prev[entityName],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleFieldChange = (entityName, fieldIndex, field, value) => {
    setConfigs(prev => ({
      ...prev,
      [entityName]: {
        ...prev[entityName],
        fields: prev[entityName].fields.map((f, index) => 
          index === fieldIndex ? { ...f, [field]: value } : f
        )
      }
    }));
    setHasChanges(true);
  };

  const handleAddField = (entityName) => {
    const entityConfig = configs[entityName] || {};
    
    // Determine which field list to use based on selected tab
    let targetFields, used;
    if (selectedTab && entityConfig.supportsTabs && entityConfig.tabFields) {
      targetFields = entityConfig.tabFields[selectedTab] || [];
      used = new Set(targetFields.map(f => f.fieldName));
    } else {
      targetFields = entityConfig.fields || [];
      used = new Set(targetFields.map(f => f.fieldName));
    }
    
    const allNames = Object.keys(availableFields || {});
    const nextName = allNames.find(n => !used.has(n));

    if (!nextName) {
      toast.error('No more available fields to add');
      return;
    }

    const base = availableFields[nextName] || {};
    const newField = {
      fieldName: nextName,
      displayLabel: base.label || base.displayName || nextName,
      isVisible: true,
      displayOrder: targetFields.length,
      width: '150px',
      alignment: 'left',
      formatType: 'text',
      isSortable: true,
      isFilterable: true,
      isSearchable: true
    };

    setConfigs(prev => {
      const updated = { ...prev };
      if (selectedTab && entityConfig.supportsTabs && entityConfig.tabFields) {
        // Add to tab-specific fields
        updated[entityName] = {
          ...prev[entityName],
          tabFields: {
            ...prev[entityName].tabFields,
            [selectedTab]: [...targetFields, newField]
          }
        };
      } else {
        // Add to general fields
        updated[entityName] = {
          ...prev[entityName],
          fields: [...targetFields, newField]
        };
      }
      return updated;
    });
    setHasChanges(true);
  };

  const handleSelectField = (entityName, fieldIndex, newFieldName) => {
    const entityConfig = configs[entityName] || {};
    
    // Determine which field list to use based on selected tab
    let currentFields;
    if (selectedTab && entityConfig.supportsTabs && entityConfig.tabFields) {
      currentFields = [...(entityConfig.tabFields[selectedTab] || [])];
    } else {
      currentFields = [...(entityConfig.fields || [])];
    }
    
    const base = (availableFields || {})[newFieldName] || {};
    const current = currentFields[fieldIndex] || {};
    currentFields[fieldIndex] = {
      ...current,
      fieldName: newFieldName,
      // If displayLabel is empty or equals previous fieldName, set from base
      displayLabel: (!current.displayLabel || current.displayLabel === current.fieldName)
        ? (base.label || base.displayName || newFieldName)
        : current.displayLabel
    };
    
    setConfigs(prev => {
      const updated = { ...prev };
      if (selectedTab && entityConfig.supportsTabs && entityConfig.tabFields) {
        // Update tab-specific fields
        updated[entityName] = {
          ...prev[entityName],
          tabFields: {
            ...prev[entityName].tabFields,
            [selectedTab]: currentFields
          }
        };
      } else {
        // Update general fields
        updated[entityName] = {
          ...prev[entityName],
          fields: currentFields
        };
      }
      return updated;
    });
    setHasChanges(true);
  };

  const handleRemoveField = (entityName, fieldIndex) => {
    const entityConfig = configs[entityName] || {};
    
    setConfigs(prev => {
      const updated = { ...prev };
      if (selectedTab && entityConfig.supportsTabs && entityConfig.tabFields) {
        // Remove from tab-specific fields
        updated[entityName] = {
          ...prev[entityName],
          tabFields: {
            ...prev[entityName].tabFields,
            [selectedTab]: prev[entityName].tabFields[selectedTab].filter((_, index) => index !== fieldIndex)
          }
        };
      } else {
        // Remove from general fields
        updated[entityName] = {
          ...prev[entityName],
          fields: prev[entityName].fields.filter((_, index) => index !== fieldIndex)
        };
      }
      return updated;
    });
    setHasChanges(true);
  };

  const moveField = (entityName, fieldIndex, direction) => {
    const entityConfig = configs[entityName] || {};
    
    // Determine which field list to use based on selected tab
    let fields;
    if (selectedTab && entityConfig.supportsTabs && entityConfig.tabFields) {
      fields = [...(entityConfig.tabFields[selectedTab] || [])];
    } else {
      fields = [...(entityConfig.fields || [])];
    }
    
    const targetIndex = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= fields.length) return;

    [fields[fieldIndex], fields[targetIndex]] = [fields[targetIndex], fields[fieldIndex]];
    fields.forEach((field, index) => field.displayOrder = index);

    setConfigs(prev => {
      const updated = { ...prev };
      if (selectedTab && entityConfig.supportsTabs && entityConfig.tabFields) {
        // Update tab-specific fields
        updated[entityName] = {
          ...prev[entityName],
          tabFields: {
            ...prev[entityName].tabFields,
            [selectedTab]: fields
          }
        };
      } else {
        // Update general fields
        updated[entityName] = {
          ...prev[entityName],
          fields
        };
      }
      return updated;
    });
    setHasChanges(true);
  };

  const exportConfigs = () => {
    const dataStr = JSON.stringify(configs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'global_listing_configs.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const importConfigs = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedConfigs = JSON.parse(e.target.result);
        setConfigs(importedConfigs);
        setHasChanges(true);
        toast.success('Configurations imported successfully');
      } catch (error) {
        toast.error('Invalid JSON file');
      }
    };
    reader.readAsText(file);
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Are you sure you want to reset all configurations to defaults? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch('/api/admin/global-listing-configs/reset', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to reset configurations');
      }

      const data = await response.json();
      setConfigs(data.configs);
      setHasChanges(false);
      toast.success('Configurations reset to defaults');
    } catch (error) {
      console.error('Error resetting configs:', error);
      toast.error('Failed to reset configurations');
    }
  };

  const currentConfig = (() => {
    const config = configs[selectedEntity];
    if (!config) return null;
    
    // If a specific tab is selected and the config supports tabs, return tab-specific config
    if (selectedTab && config.supportsTabs && config.tabFields && config.tabFields[selectedTab]) {
      return {
        ...config,
        fields: config.tabFields[selectedTab],
        tabInfo: {
          key: selectedTab,
          label: config.tabs[selectedTab]?.label || selectedTab,
          filter: config.tabs[selectedTab]?.filter
        }
      };
    }
    
    return config;
  })();
  
  // Debug logging
  console.log('🔍 Current state:', {
    configs,
    selectedEntity,
    currentConfig,
    configsKeys: Object.keys(configs)
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading global configurations...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <Globe className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            <span>Global Listing Configuration Manager</span>
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Manage global listing configurations that serve as fallbacks for all tenants
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadGlobalConfigs}
            className="bg-gray-600 dark:bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Reload</span>
          </button>
          <button
            onClick={async () => {
              try {
                const response = await fetch('/api/public/global-listing-configs');
                const data = await response.json();
                console.log('🔍 Public endpoint test:', data);
                setConfigs(data.configs || {});
                toast.success('Loaded from public endpoint');
              } catch (error) {
                console.error('❌ Public endpoint test failed:', error);
                toast.error('Public endpoint test failed');
              }
            }}
            className="bg-yellow-600 dark:bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 dark:hover:bg-yellow-600 flex items-center space-x-2"
          >
            <Globe className="h-4 w-4" />
            <span>Test Public</span>
          </button>
          <button
            onClick={exportConfigs}
            className="bg-green-600 dark:bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <label className="bg-blue-600 dark:bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center space-x-2 cursor-pointer">
            <Upload className="h-4 w-4" />
            <span>Import</span>
            <input
              type="file"
              accept=".json"
              onChange={importConfigs}
              className="hidden"
            />
          </label>
          <button
            onClick={resetToDefaults}
            className="bg-orange-600 dark:bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-700 dark:hover:bg-orange-600 flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Reset to Defaults</span>
          </button>
          <button
            onClick={saveGlobalConfigs}
            disabled={!hasChanges || saving}
            className="bg-purple-600 dark:bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>

      {/* Status Bar */}
      {hasChanges && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <span className="text-yellow-800 dark:text-yellow-200 font-medium">
              You have unsaved changes
            </span>
          </div>
        </div>
      )}

      {/* Data Status */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <span className="text-blue-800 dark:text-blue-200 font-medium">
              Configuration Status
            </span>
          </div>
          <div className="text-sm text-blue-700 dark:text-blue-300">
            Loaded {Object.keys(configs).length} entities: {Object.keys(configs).join(', ') || 'None'}
          </div>
        </div>
      </div>

      {/* Entity Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Select Entity to Configure
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {availableEntities.map((entity) => (
            <button
              key={entity.name}
              onClick={() => setSelectedEntity(entity.name)}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedEntity === entity.name
                  ? `border-${entity.color}-600 dark:border-${entity.color}-400 bg-${entity.color}-50 dark:bg-${entity.color}-900/20`
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
            >
              <div className="text-3xl mb-2">{entity.icon}</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {entity.label}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {configs[entity.name]?.fields?.length || 0} fields
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Selector (only show if entity supports tabs) */}
      {availableTabs.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Tab Configuration
          </label>
          <div className="flex space-x-3">
            <button
              onClick={() => setSelectedTab(null)}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                selectedTab === null
                  ? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
              }`}
            >
              <div className="text-sm font-medium">General Configuration</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Default fields for all tabs</div>
            </button>
            {availableTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setSelectedTab(tab.key)}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  selectedTab === tab.key
                    ? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                }`}
              >
                <div className="text-sm font-medium">{tab.label}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Tab-specific fields</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Configuration Editor */}
      {currentConfig ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                <Settings className="h-5 w-5" />
                <span>{selectedEntity} Configuration</span>
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-sm bg-gray-600 dark:bg-gray-500 text-white px-3 py-1.5 rounded hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center space-x-1"
                >
                  <Eye className="h-3 w-3" />
                  <span>{showPreview ? 'Hide' : 'Show'} Preview</span>
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* General Settings */}
            <div className="space-y-4 mb-8">
              <h3 className="text-md font-medium text-gray-900 dark:text-white">General Settings</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Configuration Name
                  </label>
                  <input
                    type="text"
                    value={currentConfig.configName || ''}
                    onChange={(e) => handleConfigChange(selectedEntity, 'configName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Page Size
                  </label>
                  <input
                    type="number"
                    value={currentConfig.configData?.pageSize || 20}
                    onChange={(e) => handleConfigChange(selectedEntity, 'configData', {
                      ...currentConfig.configData,
                      pageSize: parseInt(e.target.value)
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default Sort Field
                  </label>
                  <input
                    type="text"
                    value={currentConfig.configData?.defaultSort?.field || ''}
                    onChange={(e) => handleConfigChange(selectedEntity, 'configData', {
                      ...currentConfig.configData,
                      defaultSort: {
                        ...currentConfig.configData?.defaultSort,
                        field: e.target.value
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default Sort Order
                  </label>
                  <select
                    value={currentConfig.configData?.defaultSort?.order || 'DESC'}
                    onChange={(e) => handleConfigChange(selectedEntity, 'configData', {
                      ...currentConfig.configData,
                      defaultSort: {
                        ...currentConfig.configData?.defaultSort,
                        order: e.target.value
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="ASC">Ascending</option>
                    <option value="DESC">Descending</option>
                  </select>
                </div>
              </div>

              {/* Feature Toggles */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Features
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { key: 'enableSearch', label: 'Search' },
                    { key: 'enableFilters', label: 'Filters' },
                    { key: 'enableExport', label: 'Export' },
                    { key: 'enableColumnResize', label: 'Column Resize' },
                    { key: 'enableColumnReorder', label: 'Column Reorder' },
                    { key: 'compactMode', label: 'Compact Mode' },
                    { key: 'showRowNumbers', label: 'Row Numbers' }
                  ].map((feature) => (
                    <label key={feature.key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={currentConfig.configData?.[feature.key] || false}
                        onChange={(e) => handleConfigChange(selectedEntity, 'configData', {
                          ...currentConfig.configData,
                          [feature.key]: e.target.checked
                        })}
                        className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{feature.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Fields Configuration */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-md font-medium text-gray-900 dark:text-white">
                  Fields Configuration ({currentConfig.fields?.length || 0} fields)
                </h3>
                <button
                  onClick={() => handleAddField(selectedEntity)}
                  className="bg-green-600 dark:bg-green-500 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 flex items-center space-x-1 text-sm"
                >
                  <Plus className="h-3 w-3" />
                  <span>Add Field</span>
                </button>
              </div>

              {currentConfig.fields?.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No fields configured. Click "Add Field" to start.
                </div>
              ) : (
                <div className="space-y-3">
                  {currentConfig.fields.map((field, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => moveField(selectedEntity, index, 'up')}
                            disabled={index === 0}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30"
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => moveField(selectedEntity, index, 'down')}
                            disabled={index === currentConfig.fields.length - 1}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30"
                          >
                            ↓
                          </button>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Field {index + 1}
                          </span>
                        </div>
                        <button
                          onClick={() => handleRemoveField(selectedEntity, index)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Field Name *
                          </label>
                          <select
                            value={field.fieldName}
                            onChange={(e) => handleSelectField(selectedEntity, index, e.target.value)}
                            disabled={fieldsLoading}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                          >
                            <option value="" disabled>{fieldsLoading ? 'Loading fields...' : 'Select a field'}</option>
                            {Object.keys(availableFields || {}).map((name) => (
                              <option key={name} value={name}>
                                {name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Display Label
                          </label>
                          <input
                            type="text"
                            value={field.displayLabel}
                            onChange={(e) => handleFieldChange(selectedEntity, index, 'displayLabel', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Format Type
                          </label>
                          <select
                            value={field.formatType}
                            onChange={(e) => handleFieldChange(selectedEntity, index, 'formatType', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                          >
                            <option value="text">Text</option>
                            <option value="email">Email</option>
                            <option value="phone">Phone</option>
                            <option value="url">URL</option>
                            <option value="date">Date</option>
                            <option value="datetime">Date Time</option>
                            <option value="currency">Currency</option>
                            <option value="number">Number</option>
                            <option value="badge">Badge</option>
                            <option value="boolean">Boolean</option>
                            <option value="reference">Reference</option>
                            <option value="custom">Custom</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Width
                          </label>
                          <input
                            type="text"
                            value={field.width}
                            onChange={(e) => handleFieldChange(selectedEntity, index, 'width', e.target.value)}
                            placeholder="e.g., 150px, 20%, auto"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>

                      <div className="flex items-center space-x-3 mt-3">
                        <label className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            checked={field.isVisible}
                            onChange={(e) => handleFieldChange(selectedEntity, index, 'isVisible', e.target.checked)}
                            className="h-3 w-3 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                          />
                          <span className="text-xs text-gray-700 dark:text-gray-300">Visible</span>
                        </label>
                        <label className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            checked={field.isSortable}
                            onChange={(e) => handleFieldChange(selectedEntity, index, 'isSortable', e.target.checked)}
                            className="h-3 w-3 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                          />
                          <span className="text-xs text-gray-700 dark:text-gray-300">Sortable</span>
                        </label>
                        <label className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            checked={field.isFilterable}
                            onChange={(e) => handleFieldChange(selectedEntity, index, 'isFilterable', e.target.checked)}
                            className="h-3 w-3 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                          />
                          <span className="text-xs text-gray-700 dark:text-gray-300">Filterable</span>
                        </label>
                        <label className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            checked={field.isSearchable}
                            onChange={(e) => handleFieldChange(selectedEntity, index, 'isSearchable', e.target.checked)}
                            className="h-3 w-3 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                          />
                          <span className="text-xs text-gray-700 dark:text-gray-300">Searchable</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-8">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Configuration Found
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              No global configuration found for {selectedEntity}. The configuration may not exist or failed to load.
            </p>
            <div className="space-y-2 text-sm text-gray-500 dark:text-gray-400">
              <p>Available configurations: {Object.keys(configs).join(', ') || 'None'}</p>
              <p>Selected entity: {selectedEntity}</p>
            </div>
            <button
              onClick={loadGlobalConfigs}
              className="mt-4 bg-blue-600 dark:bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center space-x-2 mx-auto"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Reload Configurations</span>
            </button>
          </div>
        </div>
      )}

      {/* Info Panel */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">About Global Listing Configurations</p>
            <ul className="space-y-1 text-xs">
              <li>• These configurations serve as fallbacks when tenants don't have custom configurations</li>
              <li>• Changes affect all tenants that don't have their own configurations</li>
              <li>• Use the export/import feature to backup and restore configurations</li>
              <li>• Field names must match the database column names exactly</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalListingConfigManager;
